/**
 * State shape for the Get a Quote wizard.
 * Holds the current step and draft data that later slices will populate.
 */

import { UsStateCode } from '@/enterprise/components/InstantPay/usStates';
import { CreateMonduQuoteBody, MonduQuote } from '@/enterprise/data/useCreateMonduQuote';
import { ANNUAL_REVENUE_BANDS } from './content';

/** Each value is also the address segment that names the step. See `quoteWizardRouting.ts`. */
export type QuoteWizardStep = 'business-details' | 'add-customers' | 'review-quote';

/** Identifies the selected annual revenue band. Labels and figures live on `ANNUAL_REVENUE_BANDS`. */
export type AnnualRevenueRangeId = (typeof ANNUAL_REVENUE_BANDS)[number]['id'];

/**
 * Step 1 values, shaped so the quote request can be built from them without re-reading the form.
 */
export interface BusinessDetailsDraft {
  /** Prefilled from the logged-in user, but editable — the contact need not be the account holder. */
  firstName: string;
  lastName: string;
  emailAddress: string;
  /** As typed. Separators and country codes are kept; only the digit count is validated. */
  phoneNumber: string;
  /** Omitted from the quotes body when empty, so SEA falls back to the account company name. */
  companyName: string;
  addressLine1: string;
  city: string;
  /** Form only. The SEA quotes body has no state field. */
  state: UsStateCode;
  zipCode: string;
  /**
   * The selected band, and the only revenue input the form offers. `annualRevenueFigure` turns it
   * into the `revenue: number` the quotes body requires.
   */
  revenueRange: AnnualRevenueRangeId;
}

/**
 * 5-digit US zip, optionally with a 4-digit extension. Shared by Business Details and Add Customers
 * so the two steps cannot drift onto different rules.
 */
export const US_ZIP_PATTERN = /^\d{5}(-\d{4})?$/;

/**
 * Every customer is in the US — state and zip are US-only — so country is a constant rather than a
 * field. SEA types `countryCode` as a plain string, so nothing downstream would reject a country name.
 */
export const CUSTOMER_COUNTRY = { code: 'US', name: 'United States' } as const;

/** The address parts a saved customer always has, whether the catalog supplied them or the supplier typed them. */
interface CustomerAddress {
  street: string;
  city: string;
  state: UsStateCode;
  zip: string;
}

/**
 * One row of the step 2 Added Customers list. The quotes POST sends each row, with `volume` parsed
 * into `expectedMonthlyInvoiceVolume`.
 */
export interface AddedCustomer {
  /** Null when the name was typed instead of picked. Still a valid row — Mondu fuzzy-matches it. */
  entityId: string | null;
  /** What Mondu is asked to match, picked or typed. */
  name: string;
  address: CustomerAddress;
  /** As typed. The added-customers row formats this as USD. `parseMonthlyInvoiceVolume` is what the quotes POST sends. */
  volume: string;
}

const VOLUME_FORMATTING = /[$,\s]/g;
const VOLUME_NUMBER = /^\d+(\.\d+)?$/;

/**
 * The number sent as `expectedMonthlyInvoiceVolume`. Blank is null. `$`, commas, and spaces are
 * ignored, so `$125,000.00` is `125000`. A value that is not a number after that is `NaN`.
 */
export const parseMonthlyInvoiceVolume = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;

  const normalized = trimmed.replace(VOLUME_FORMATTING, '');
  if (!VOLUME_NUMBER.test(normalized)) return Number.NaN;

  return Number(normalized);
};

/**
 * Row identity, used for the React key, the duplicate check and removal. The normalized name rather
 * than the entity id, because a typed name has none.
 */
export const customerKey = (name: string): string => name.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * The figure for a band. Every id comes from `ANNUAL_REVENUE_BANDS`, and that table's `satisfies`
 * clause rejects a band with no figure, so this cannot return `undefined`.
 */
export const annualRevenueFigure = (rangeId: AnnualRevenueRangeId): number => {
  const band = ANNUAL_REVENUE_BANDS.find((candidate) => candidate.id === rangeId);
  if (!band) {
    throw new Error(`No revenue figure for band ${rangeId}`);
  }

  return band.figure;
};

/**
 * Draft → quotes request body. Business Details `state` stays off the body: the supplier address
 * on this contract has no state field.
 *
 * `registrationNumber` is absent for a different reason — the step stopped asking for it. SEA
 * distinguishes an omitted value from an empty one: omitting leaves the field off the ENF supplier
 * profile, whereas `''` is forwarded and stored as an empty string. Omitting is the honest shape,
 * since the supplier was never asked.
 *
 * A blank volume is `null`. A volume that does not parse is also `null`, so the body never carries
 * `NaN`. The form refuses that value before it can be saved.
 */
export const buildCreateQuoteBody = (
  draft: BusinessDetailsDraft,
  customers: AddedCustomer[]
): CreateMonduQuoteBody => ({
  // Omitted rather than sent empty, so SEA falls back to the company name on the account.
  ...(draft.companyName.trim() ? { companyName: draft.companyName.trim() } : {}),
  revenue: annualRevenueFigure(draft.revenueRange),
  registeredAddress: {
    addressLine1: draft.addressLine1.trim(),
    city: draft.city.trim(),
    zipCode: draft.zipCode.trim(),
  },
  customers: customers.map((customer) => {
    const volume = parseMonthlyInvoiceVolume(customer.volume);

    return {
      name: customer.name,
      addressLine1: customer.address.street,
      city: customer.address.city,
      state: customer.address.state,
      zipCode: customer.address.zip,
      countryCode: CUSTOMER_COUNTRY.code,
      expectedMonthlyInvoiceVolume: Number.isFinite(volume) ? volume : null,
    };
  }),
});

export interface QuoteWizardState {
  step: QuoteWizardStep;
  businessDetails: BusinessDetailsDraft | null;
  customers: AddedCustomer[];
  /** The create-quote response, kept for Review Quote. Null until a `quoted` quote comes back. */
  quote: MonduQuote | null;
}

export const initialWizardState: QuoteWizardState = {
  step: 'business-details',
  businessDetails: null,
  customers: [],
  quote: null,
};
